/**
 * The Level Creator's model (spec 2026-11-01): the catalogues the settings
 * screen and the editor pick from, a project's shape, and how a project
 * becomes a playable `LevelDef` for a TEST run.
 *
 * Pure — no Phaser, no storage. A project is private to its author until it
 * is PUBLISHED and an admin approves that exact version (spec 2026-11-02 §0):
 * only the approved snapshot is ever shown to another player.
 */
import type { CreatorPieceDef, LevelDef } from '../core/game';
import type { Vec2 } from '../core/vec';
import type { ArenaWaveEntry, EnemyType } from '../core/types';
import type { WaveMods } from '../core/waves';
import { arenaWaveGroups } from '../core/arenaWave';
import { BALANCE } from './balance';
import { ENEMY_NAMES, TOWER_NAMES } from './displayNames';
import { LEVELS, chapterOf, levelsInChapter, musicKeyFor } from './levels';
import {
  GLOW_CLUSTERS,
  GOLDEN_STATUES,
  HUB_CHAPTERS,
  HUB_TOWER_HP,
  MUSHY_STATUE,
  SHROOM_GRAVES,
  SHROOM_VILLAGE_HOUSES,
  TREE_HOUSES,
} from './hubLevels';
import {
  EVIL_ORB_DECOR,
  HEAVENS_PEAK_RING,
  ORIGIN_CHAPTERS,
  RED_PHANTOM_DECOR,
} from './originEvent';

/** The one id every TEST run plays under (`findLevel` resolves it). */
export const CREATOR_LEVEL_ID = 950;
/** The one id every Explore run plays under — outside the scored 1–299. */
export const EXPLORE_LEVEL_ID = 951;

export const MAX_PROJECTS = 20;
export const MAX_NAME = 30;
export const MAX_PLOTS = 40;
export const MAX_WAVES = 50;
/** Rows in one wave's mix — mirrored by save_creator_level's validator. */
export const MAX_WAVE_ENTRIES = 60;
export const MAX_PIECES = 300;
export const SCALE_MIN = 0.1;
export const SCALE_MAX = 5;
/** "Like a Hub level": the lowest Hub/Origin start (1500), 20 lives, tower HP. */
export const CREATOR_START_CASH = 1500;
/** The Starting Money selector's range — mirrored by creator_def_valid. */
export const START_CASH_MIN = 500;
export const START_CASH_MAX = 100000;

/** One ◀ / ▶ press: $500 steps up to $10,000, then $5,000 steps. */
export function stepStartCash(cash: number, dir: 1 | -1): number {
  const c = clampStartCash(cash);
  const next = dir > 0 ? c + (c >= 10000 ? 5000 : 500) : c - (c > 10000 ? 5000 : 500);
  return clampStartCash(next);
}

export function clampStartCash(cash: unknown): number {
  if (typeof cash !== 'number' || !Number.isFinite(cash)) return CREATOR_START_CASH;
  return Math.min(START_CASH_MAX, Math.max(START_CASH_MIN, Math.round(cash)));
}

/** A project's starting money (older projects have none: the 1500 default). */
export function startCashOf(p: { startCash?: number }): number {
  return p.startCash === undefined ? CREATOR_START_CASH : clampStartCash(p.startCash);
}
export const CREATOR_START_LIVES = 20;

export const DUPLICATE_NAME = 'You have already named a level this name';
export const EMPTY_NAME = 'Give your level a name';
export const LONG_NAME = `Names can be ${MAX_NAME} letters at most`;

export type PieceLayer = 'decor' | 'top' | 'text';
/** A textbox's longest line — mirrored by creator_def_valid. */
export const TEXT_MAX = 40;
export const TEXT_KEY = 'creator-text';
export const TEXT_DEFAULT = 'Text';
/** A textbox's font size at scale 1. */
export const TEXT_SIZE = 28;
export type CreatorPiece = CreatorPieceDef;

export interface CreatorWave {
  entries: ArenaWaveEntry[];
  interval: number;
  mods: WaveMods;
}

export interface CreatorProject {
  v: 1;
  id: string;
  name: string;
  template: string;
  song: string;
  track: string;
  background: string;
  showPicture: boolean;
  pieces: CreatorPiece[];
  plots: Vec2[];
  waves: CreatorWave[];
  peaceful: boolean;
  /** Starting money, START_CASH_MIN..MAX; absent = CREATOR_START_CASH. */
  startCash?: number;
  /** Its row's border colour in Explore and in a chapter (CREATOR_BORDERS id). */
  border?: string;
  /**
   * What the player writes about the level (the information page). Never in
   * the server's `def`: it travels on its own and is redacted on save.
   */
  description?: string;
  updatedAt: number;
}

// ── Templates ────────────────────────────────────────────────────────────────

export interface CreatorTemplate {
  /** Safe slug, e.g. `c1-e4`, `hc2-l8`, `ec1-l3`, `blank`. */
  id: string;
  /** What the selector shows: `C1-E4`, `HC2-L8`, `EC1-L3`, `Blank`. */
  label: string;
  /** The level's real name, printed small under the label. */
  name: string;
  levelId: number | null;
  /** A picture-only template (no level): its painted map. */
  picture?: string;
}

function buildTemplates(): CreatorTemplate[] {
  const out: CreatorTemplate[] = [
    { id: 'blank', label: 'Blank', name: 'A white map', levelId: null },
    {
      id: 'grassland',
      label: 'Grassland',
      name: 'A plain green meadow',
      levelId: null,
      picture: 'bg-creator-grassland',
    },
    {
      id: 'beach',
      label: 'Empty Beach',
      name: 'Sand, and the ocean beyond',
      levelId: null,
      picture: 'bg-creator-beach',
    },
    {
      id: 'hills',
      label: 'Hills',
      name: 'Grassland with big hills',
      levelId: null,
      picture: 'bg-creator-hills',
    },
  ];
  const chapters = [...new Set(LEVELS.map(chapterOf))].sort((a, b) => a - b);
  for (const ch of chapters) {
    levelsInChapter(ch).forEach((l, i) =>
      out.push({ id: `c${ch}-e${i + 1}`, label: `C${ch}-E${i + 1}`, name: l.name, levelId: l.id }),
    );
  }
  for (const c of HUB_CHAPTERS) {
    c.levels.forEach((l, i) =>
      out.push({
        id: `hc${c.number}-l${i + 1}`,
        label: `HC${c.number}-L${i + 1}`,
        name: l.name,
        levelId: l.id,
      }),
    );
  }
  // EC1 is Above the Clouds, EC2 The Dark Shadows (the user's numbering).
  const ec: [number, string][] = [
    [1, 'clouds'],
    [2, 'shadows'],
  ];
  for (const [n, id] of ec) {
    const chapter = ORIGIN_CHAPTERS.find((c) => c.id === id);
    chapter?.levels.forEach((l, i) =>
      out.push({ id: `ec${n}-l${i + 1}`, label: `EC${n}-L${i + 1}`, name: l.name, levelId: l.id }),
    );
  }
  return out;
}

export const CREATOR_TEMPLATES: readonly CreatorTemplate[] = buildTemplates();

const ALL_TEMPLATE_LEVELS: LevelDef[] = [
  ...LEVELS,
  ...HUB_CHAPTERS.flatMap((c) => c.levels),
  ...ORIGIN_CHAPTERS.flatMap((c) => c.levels),
];

function levelById(id: number | null | undefined): LevelDef | undefined {
  return id == null ? undefined : ALL_TEMPLATE_LEVELS.find((l) => l.id === id);
}

export function findTemplate(id: string): CreatorTemplate {
  return CREATOR_TEMPLATES.find((t) => t.id === id) ?? CREATOR_TEMPLATES[0];
}

/** The painted map of a template (`undefined` for Blank). */
export function templatePictureKey(templateId: string): string | undefined {
  const t = findTemplate(templateId);
  return t.picture ?? levelById(t.levelId)?.background;
}

/**
 * Grassland, Empty Beach and Hills ARE their picture, so it is always shown
 * (a project saved with the box off still draws it); a level template's
 * picture follows the "Show template picture" box.
 */
export function templateIsPicture(templateId: string): boolean {
  return findTemplate(templateId).picture !== undefined;
}

/** The picture a project draws under everything, or none. */
export function projectPictureKey(
  p: Pick<CreatorProject, 'template' | 'showPicture'>,
): string | undefined {
  return p.showPicture || templateIsPicture(p.template)
    ? templatePictureKey(p.template)
    : undefined;
}

// ── Songs ────────────────────────────────────────────────────────────────────

/**
 * Every level track in assets/music, labelled by its file name (which already
 * reads C1-E1_E2 / HC-L5 / …). The menu themes are not level songs.
 */
const SONG_FILES = [
  'C1-E1_E2',
  'C1-E3_E4',
  'C1-E5',
  'C2-E1_E2',
  'C2-E3_E4',
  'C2-E5',
  'C2-E6_E7',
  'C2-E8_E9_E10',
  'C3-E1_E2',
  'C3-E3_E4',
  'C3-E5',
  'C3-E6_E7',
  'C3-E8_E9',
  'C3-E10',
  'HC-L1_L2',
  'HC-L3_L4',
  'HC-L5',
  'HC-L6_L7',
  'HC-L8_L9',
  'Origin-D1_D2',
] as const;

export const CREATOR_SONGS: readonly { id: string; label: string }[] = [
  { id: 'none', label: 'None' },
  ...SONG_FILES.map((f) => ({ id: f, label: f })),
];

/** The song a template's level plays, or `none` (Blank, unscored chapters). */
export function templateSongFor(templateId: string): string {
  const level = levelById(findTemplate(templateId).levelId);
  if (!level) return 'none';
  const key = musicKeyFor(level);
  return CREATOR_SONGS.some((s) => s.id === key) ? key : 'none';
}

// ── Tracks ───────────────────────────────────────────────────────────────────

export interface CreatorTrack {
  id: string;
  label: string;
  levelId: number;
  path: Vec2[];
  entrances?: Vec2[][];
  trackStyle?: LevelDef['trackStyle'];
}

/** The curated dozen, chosen for variety of shape (and the three styles). */
const TRACK_PICKS: [string, string, number][] = [
  ['classic', 'The Classic', 1],
  ['temple-road', 'Long Temple Road', 20],
  ['heavens-ring', "Heaven's Ring", 215],
  ['twin-craters', 'Twin Craters', 108],
  ['triple-gate', 'Triple Gate', 110],
  ['top-to-bottom', 'Top to Bottom', 203],
  ['shroom-marathon', 'Shroom Marathon', 121],
  ['ember-winding', 'Ember Winding', 25],
  ['mine-rails', 'Mine Rails', 11],
  ['lava-rocks', 'Lava Rocks', 21],
  ['station-panels', 'Station Panels', 105],
  ['mushy-loop', 'Mushy Loop', 115],
];

export const CREATOR_TRACKS: readonly CreatorTrack[] = TRACK_PICKS.flatMap(
  ([id, label, levelId]) => {
    const l = levelById(levelId);
    return l
      ? [{ id, label, levelId, path: l.path, entrances: l.entrances, trackStyle: l.trackStyle }]
      : [];
  },
);

export function findTrack(id: string): CreatorTrack {
  return CREATOR_TRACKS.find((t) => t.id === id) ?? CREATOR_TRACKS[0];
}

/** The template's own track if it is on the list, else the first. */
export function templateTrackFor(templateId: string): string {
  const levelId = findTemplate(templateId).levelId;
  return CREATOR_TRACKS.find((t) => t.levelId === levelId)?.id ?? CREATOR_TRACKS[0].id;
}

// ── Backgrounds ──────────────────────────────────────────────────────────────

export interface CreatorBackground {
  id: string;
  label: string;
  color?: number;
  key?: string;
}

const COLOURS: [string, string, number][] = [
  ['red', 'Red', 0xd93636],
  ['crimson', 'Crimson', 0x8f1d2c],
  ['orange', 'Orange', 0xf08a24],
  ['amber', 'Amber', 0xf5b82e],
  ['yellow', 'Yellow', 0xf2e14c],
  ['lime', 'Lime', 0xa6d93b],
  ['green', 'Green', 0x3fa34d],
  ['forest', 'Forest', 0x24603a],
  ['teal', 'Teal', 0x2a9d8f],
  ['cyan', 'Cyan', 0x48cae4],
  ['sky', 'Sky', 0x8fd3ff],
  ['blue', 'Blue', 0x2f6fde],
  ['navy', 'Navy', 0x1d2b64],
  ['indigo', 'Indigo', 0x4b3fb0],
  ['purple', 'Purple', 0x8a3fc4],
  ['pink', 'Pink', 0xf28fc3],
  ['magenta', 'Magenta', 0xc4318f],
  ['brown', 'Brown', 0x7a4b2a],
  ['sand', 'Sand', 0xe0c88f],
  ['white', 'White', 0xffffff],
  ['silver', 'Silver', 0xc8ccd2],
  ['grey', 'Grey', 0x7c828a],
  ['charcoal', 'Charcoal', 0x34383f],
  ['black', 'Black', 0x000000],
];

const SPECIALS: [string, string][] = [
  ['volcano', 'Volcanoes'],
  ['mountains', 'Mountains'],
  ['ocean', 'Ocean'],
  ['space', 'Outer Space'],
  ['desert', 'Desert'],
  ['snow', 'Snowy Peaks'],
];

export const CREATOR_BACKGROUNDS: readonly CreatorBackground[] = [
  ...COLOURS.map(([id, label, color]) => ({ id, label, color })),
  ...SPECIALS.map(([id, label]) => ({ id, label, key: `bg-creator-${id}` })),
];

export function findBackground(id: string): CreatorBackground {
  return CREATOR_BACKGROUNDS.find((b) => b.id === id) ?? CREATOR_BACKGROUNDS[19];
}

// ── Items (the editor's three lists) ─────────────────────────────────────────

export type ItemTab = 'decor' | 'top' | 'special';

export interface CreatorItem {
  key: string;
  label: string;
  tab: ItemTab;
  /** Longest side in px at scale 1 (a textbox: its font size). */
  size: number;
  /** Render-only motion: `float` bobs up and down. */
  anim?: 'float';
  /** Render-only light: a soft pulsing glow of this colour. */
  glow?: number;
  /** Snaps to a grid of its own size, so neighbours meet (water, river). */
  snap?: boolean;
}

type ItemExtras = Pick<CreatorItem, 'anim' | 'glow' | 'snap'>;

const item = (
  key: string,
  label: string,
  tab: ItemTab,
  size: number,
  extras: ItemExtras = {},
): CreatorItem => ({ key, label, tab, size, ...extras });

/** Enemy forms with no sprite of their own (drawn from their first form). */
const NO_ENEMY_ART = new Set(['halolight2', 'halolight3']);

/** Every tower, as a DECOR piece — the Hambi Tower first. */
const TOWER_ITEMS: CreatorItem[] = (Object.keys(TOWER_NAMES) as (keyof typeof TOWER_NAMES)[]).map(
  (t) => item(`tower-${t}-1`, TOWER_NAMES[t], 'decor', 72),
);

/** Every enemy, as a TOP piece. */
const ENEMY_ITEMS: CreatorItem[] = (Object.keys(ENEMY_NAMES) as (keyof typeof ENEMY_NAMES)[])
  .filter((e) => !NO_ENEMY_ART.has(e))
  .map((e) => item(`enemy-${e}`, ENEMY_NAMES[e], 'top', 64));

export const CREATOR_ITEMS: readonly CreatorItem[] = [
  // DECOR — ground pieces, under the track.
  item('decor-cr-tree-oak', 'Oak Tree', 'decor', 110),
  item('decor-cr-tree-pine', 'Pine Tree', 'decor', 110),
  item('decor-cr-tree-palm', 'Palm Tree', 'decor', 110),
  item('decor-cr-tree-dead', 'Dead Tree', 'decor', 100),
  item('decor-sakura', 'Sakura Tree', 'decor', 90),
  item('decor-cr-bush', 'Bush', 'decor', 70),
  item('decor-cr-grass', 'Grass', 'decor', 50),
  item('decor-cr-grass-tall', 'Tall Grass', 'decor', 60),
  item('decor-cr-flowers', 'Flowers', 'decor', 50),
  item('decor-cr-rock', 'Rock', 'decor', 50),
  item('decor-cr-boulder', 'Boulder', 'decor', 90),
  item('decor-cr-crater', 'Crater', 'decor', 110),
  item('decor-cr-log', 'Log', 'decor', 80),
  item('decor-cr-fungi-red', 'Small Red Fungi', 'decor', 45),
  item('decor-cr-fungi-brown', 'Small Brown Fungi', 'decor', 45),
  item('decor-cr-fungi-big', 'Big Fungi', 'decor', 100),
  item('decor-bigmushroom', 'Giant Mushroom', 'decor', 110),
  item('decor-glowmushroom-blue', 'Blue Glow Fungi', 'decor', 70),
  item('decor-glowmushroom-orange', 'Orange Glow Fungi', 'decor', 70),
  item('decor-goldenmushroom', 'Golden Fungi', 'decor', 45),
  item('decor-iceblock', 'Ice Block', 'decor', 70),
  item('decor-floatingrock', 'Floating Rock', 'decor', 90),
  item('decor-cr-block-square', 'Square Block', 'decor', 80),
  item('decor-cr-block-circle', 'Round Block', 'decor', 80),
  // Round 2 (spec 2026-11-02).
  item('decor-cr-volcano', 'Volcano', 'decor', 110),
  item('decor-cr-volcano-smoke', 'Smoky Volcano', 'decor', 110),
  item('decor-cr-mountain', 'Mountain', 'decor', 110),
  item('decor-cr-mountain-snow', 'Snowy Mountain', 'decor', 110),
  item('decor-cr-mountain-range', 'Mountain Range', 'decor', 130),
  item('decor-cr-lake', 'Lake', 'decor', 120),
  item('decor-cr-lake-long', 'Long Lake', 'decor', 130),
  item('decor-cr-pond', 'Lily Pond', 'decor', 90),
  item('decor-cr-water-block', 'Water Block', 'decor', 64, { snap: true }),
  item('decor-cr-water-block-deep', 'Deep Water Block', 'decor', 64, { snap: true }),
  item('decor-cr-cabin', 'Cabin', 'decor', 110),
  item('decor-cr-cabin-snow', 'Snowy Cabin', 'decor', 110),
  item('decor-cr-boat', 'Rowing Boat', 'decor', 80),
  item('decor-cr-boat-sail', 'Sailboat', 'decor', 100),
  item('decor-cr-boat-pirate', 'Pirate Ship', 'decor', 120),
  item('decor-cr-well', 'Well', 'decor', 80),
  item('decor-cr-windmill', 'Windmill', 'decor', 120),
  item('decor-cr-watchtower', 'Watchtower', 'decor', 120),
  item('decor-cr-bridge', 'Bridge', 'decor', 110),
  item('decor-ropebridge', 'Rope Bridge', 'decor', 120),
  item('decor-cr-fence', 'Fence', 'decor', 90),
  item('decor-cr-tent', 'Tent', 'decor', 90),
  item('decor-cr-ruins', 'Ruins', 'decor', 110),
  item('decor-cr-crystal-blue', 'Blue Crystals', 'decor', 70),
  item('decor-cr-crystal-pink', 'Pink Crystals', 'decor', 70),
  item('decor-cr-crystal-green', 'Green Crystals', 'decor', 70),
  item('decor-cr-crystal-purple', 'Purple Crystals', 'decor', 70),
  item('decor-cr-crystal-gold', 'Gold Crystals', 'decor', 70),
  item('decor-cr-crystal-red', 'Red Crystals', 'decor', 70),
  item('decor-cr-crystal-big', 'Big Crystal', 'decor', 110),
  item('decor-cr-tree-shadow', 'Shadow Tree', 'decor', 120),
  item('decor-cr-tree-jungle', 'Jungle Tree', 'decor', 120),
  item('decor-cr-tree-cloud', 'Cloud Tree', 'decor', 120),
  item('decor-cr-tree-golden', 'Golden Tree', 'decor', 120),
  item('decor-cr-tree-ghost', 'Ghost Tree', 'decor', 120),
  item('decor-cr-lamppost', 'Lamppost', 'decor', 90, { glow: 0xffd77a }),
  item('decor-cr-lamppost-double', 'Double Lamppost', 'decor', 100, { glow: 0xffd77a }),
  item('item-evilorb', 'The Evil Orb', 'decor', 80, { glow: 0xa040ff }),
  ...TOWER_ITEMS,
  // TOP — always over the decor and the track.
  item('decor-cr-pumpkin', 'Pumpkin', 'top', 60),
  item('decor-cr-apple', 'Apple', 'top', 40),
  item('decor-cr-banana', 'Banana', 'top', 45),
  item('decor-cr-watermelon', 'Watermelon', 'top', 60),
  item('decor-cr-cherries', 'Cherries', 'top', 40),
  item('decor-cr-pineapple', 'Pineapple', 'top', 55),
  item('decor-cr-candy', 'Candy', 'top', 40),
  item('decor-cr-lantern', 'Lantern', 'top', 60),
  item('decor-cr-signpost', 'Signpost', 'top', 70),
  item('decor-cr-campfire', 'Campfire', 'top', 70),
  item('decor-mushystatue', 'Mushy Statue', 'top', 150),
  item('decor-treehouse-big', 'Big Tree House', 'top', 220),
  item('decor-treehouse-small', 'Small Tree House', 'top', 130),
  item('decor-shroomhouse', 'Shroom House', 'top', 100),
  item('decor-shroomgrave', 'Shroom Grave', 'top', 100),
  item('decor-goldstatue', 'Golden Statue', 'top', 120),
  item('decor-orbtree', 'Orb Tree', 'top', 150),
  item('decor-heavenorb', 'Heaven Orb', 'top', 84),
  item('decor-redphantom', 'Red Phantom', 'top', 60),
  item('decor-icesculpture', 'Ice Sculpture', 'top', 78),
  item('decor-beachbar', 'Beach Bar', 'top', 140),
  item('decor-bun', 'Bun', 'top', 50),
  item('decor-rocket', 'Rocket', 'top', 120),
  item('decor-planet-a', 'Planet', 'top', 90),
  item('decor-planet-b', 'Ringed Planet', 'top', 90),
  item('decor-planet-c', 'Ice Planet', 'top', 90),
  item('decor-discoball', 'Disco Ball', 'top', 70),
  item('decor-nachosign', 'Nacho Sign', 'top', 90),
  // Round 2 (spec 2026-11-02).
  item('decor-cr-tree-apple', 'Apple Tree', 'top', 120),
  item('decor-cr-tree-orange', 'Orange Tree', 'top', 120),
  item('decor-cr-glow-orb', 'Glow Orb', 'top', 70, { glow: 0xfff3a0 }),
  item('decor-cr-glow-lantern', 'Glow Lantern', 'top', 70, { glow: 0xffa040 }),
  item('decor-cr-glow-crystal', 'Glow Crystal', 'top', 70, { glow: 0x60e8ff }),
  item('decor-cr-poisonshroom-huge', 'Huge Poison Shroom', 'top', 160),
  item('decor-cr-poisonshroom-huge-green', 'Huge Toadstool', 'top', 160),
  item('decor-cr-floatcrystal-blue', 'Floating Gold Crystal', 'top', 64, { anim: 'float' }),
  item('decor-cr-floatcrystal-pink', 'Floating Pink Crystal', 'top', 64, { anim: 'float' }),
  item('decor-cr-floatcrystal-green', 'Floating Green Crystal', 'top', 64, { anim: 'float' }),
  item('decor-cr-iceplatform', 'Ice Platform', 'top', 120),
  item('decor-cr-iceplatform-square', 'Square Ice Platform', 'top', 120),
  item('decor-cr-cart-hotdog', 'Hot Dog Cart', 'top', 110),
  item('decor-cr-cart-burger', 'Burger Cart', 'top', 110),
  item('decor-cr-truck-burger', 'Burger Truck', 'top', 150),
  item('decor-cr-truck-taco', 'Taco Truck', 'top', 150),
  ...ENEMY_ITEMS,
  // SPECIAL — gameplay pieces, ground patches, the river, the sky, textboxes.
  item('plot', 'Plot', 'special', 64),
  item(TEXT_KEY, 'Textbox', 'special', TEXT_SIZE),
  item('decor-cr-grasspatch', 'Grass Patch', 'special', 100),
  item('decor-cr-grasspatch-big', 'Big Grass Patch', 'special', 180),
  item('decor-cr-sandpatch', 'Sand Patch', 'special', 100),
  item('decor-cr-sandpatch-big', 'Big Sand Patch', 'special', 180),
  item('decor-cr-river-straight', 'River', 'special', 128, { snap: true }),
  item('decor-cr-river-bend', 'River Bend', 'special', 128, { snap: true }),
  item('decor-cr-river-end', 'River End', 'special', 128, { snap: true }),
  item('decor-cr-river-cross', 'River Crossing', 'special', 128, { snap: true }),
  item('decor-cr-floatisland-crystal', 'Floating Crystals', 'special', 110),
  item('decor-cr-floatisland-crystal-gold', 'Floating Gold Crystals', 'special', 110),
  item('decor-cr-sun', 'Sun', 'special', 100),
  item('decor-cr-moon', 'Moon', 'special', 90),
  item('decor-cr-moon-full', 'Full Moon', 'special', 90),
];

/** The layer a new piece from this item goes on. */
export function layerFor(it: Pick<CreatorItem, 'key' | 'tab'>): PieceLayer {
  if (it.key === TEXT_KEY) return 'text';
  return it.tab === 'top' ? 'top' : 'decor';
}

export function findItem(key: string): CreatorItem | undefined {
  return CREATOR_ITEMS.find((i) => i.key === key);
}

// ── Template pieces and plots ────────────────────────────────────────────────

/** A template piece: `size` is the display size the level draws it at. */
function tplPiece(n: number, key: string, x: number, y: number, size: number): CreatorPiece {
  const natural = findItem(key)?.size ?? size;
  return {
    id: `tpl-${n}`,
    layer: 'top',
    key,
    x,
    y,
    scale: Math.round((size / natural) * 100) / 100,
    rotation: 0,
  };
}

/** The separate decoration sprites a template's level places, as pieces. */
export function templatePieces(levelId: number | null): CreatorPiece[] {
  const level = levelById(levelId);
  const out: CreatorPiece[] = [];
  const add = (key: string, x: number, y: number, size: number): void => {
    out.push(tplPiece(out.length, key, x, y, size));
  };
  switch (level?.decor) {
    case 'mushystatue':
      add('decor-mushystatue', MUSHY_STATUE[0], MUSHY_STATUE[1], MUSHY_STATUE[2]);
      break;
    case 'shroomgraves':
      for (const [x, y, s] of SHROOM_GRAVES) add('decor-shroomgrave', x, y, s);
      break;
    case 'goldstatues':
      for (const [x, y, s] of GOLDEN_STATUES) add('decor-goldstatue', x, y, s);
      break;
    case 'glowclusters':
      for (const [x, y, s, blue] of GLOW_CLUSTERS)
        add(blue ? 'decor-glowmushroom-blue' : 'decor-glowmushroom-orange', x, y, s);
      break;
    case 'treehouses': {
      const big = TREE_HOUSES[0][2];
      for (const [x, y, s] of TREE_HOUSES)
        add(s === big ? 'decor-treehouse-big' : 'decor-treehouse-small', x, y, s);
      break;
    }
    case 'shroomvillage':
      for (const [x, y, s] of SHROOM_VILLAGE_HOUSES) add('decor-shroomhouse', x, y, s);
      break;
    case 'redphantoms':
      for (const { x, y } of RED_PHANTOM_DECOR) add('decor-redphantom', x, y, 60);
      break;
    case 'evilorb':
      add('decor-orbtree', EVIL_ORB_DECOR.tree.x, EVIL_ORB_DECOR.tree.y, EVIL_ORB_DECOR.tree.size);
      break;
    case 'heavenspeak': {
      const { centre, treeRadius, trees } = HEAVENS_PEAK_RING;
      for (let i = 0; i < trees; i++) {
        const a = (i / trees) * Math.PI * 2 - Math.PI / 2;
        add(
          'decor-sakura',
          centre.x + Math.cos(a) * treeRadius,
          centre.y + Math.sin(a) * treeRadius - 14,
          72,
        );
      }
      add('decor-heavenorb', centre.x, centre.y - 6, 84);
      break;
    }
    case 'beach':
      add('decor-beachbar', 640, 114, 140);
      break;
    default:
      break;
  }
  return out.map((p) => ({ ...p, x: Math.round(p.x), y: Math.round(p.y) }));
}

function distToTrack(p: Vec2, track: CreatorTrack): number {
  let best = Infinity;
  for (const line of [track.path, ...(track.entrances ?? [])]) {
    for (let i = 1; i < line.length; i++) {
      const a = line[i - 1];
      const b = line[i];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len2 = dx * dx + dy * dy || 1;
      const u = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
      best = Math.min(best, Math.hypot(p.x - (a.x + u * dx), p.y - (a.y + u * dy)));
    }
  }
  return best;
}

/** About ten plots beside the track, alternating sides — Blank's starting set. */
export function defaultPlots(trackId: string): Vec2[] {
  const track = findTrack(trackId);
  const path = track.path;
  const total = path
    .slice(1)
    .reduce((s, q, i) => s + Math.hypot(q.x - path[i].x, q.y - path[i].y), 0);
  const out: Vec2[] = [];
  const WANT = 10;
  // Try evenly spaced spots first, then shifted ones further out, until ten fit.
  const OFFSETS = [75, 75, 110, 140];
  const SLOTS = WANT * 3;
  for (let k = 0; k < SLOTS * OFFSETS.length && out.length < WANT; k++) {
    const offset = OFFSETS[Math.floor(k / SLOTS)];
    // Walk to distance d along the path.
    let d = ((k % SLOTS) + 0.5) * (total / SLOTS);
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      const seg = Math.hypot(b.x - a.x, b.y - a.y);
      if (d > seg) {
        d -= seg;
        continue;
      }
      const ux = (b.x - a.x) / (seg || 1);
      const uy = (b.y - a.y) / (seg || 1);
      const side = (k + Math.floor(k / SLOTS)) % 2 === 0 ? 1 : -1;
      const p = {
        x: Math.round(a.x + ux * d - uy * offset * side),
        y: Math.round(a.y + uy * d + ux * offset * side),
      };
      const inside = p.x >= 60 && p.x <= 1220 && p.y >= 90 && p.y <= 680;
      const clear =
        distToTrack(p, track) >= 60 && out.every((q) => Math.hypot(q.x - p.x, q.y - p.y) >= 70);
      if (inside && clear) out.push(p);
      break;
    }
  }
  return out;
}

/** The plots a template starts with: its level's, or the default set (Blank). */
function templatePlots(templateId: string, trackId: string): Vec2[] {
  const level = levelById(findTemplate(templateId).levelId);
  return level ? level.plots.map((p) => ({ x: p.pos.x, y: p.pos.y })) : defaultPlots(trackId);
}

// ── Projects ─────────────────────────────────────────────────────────────────

export interface NewProjectOpts {
  id: string;
  name: string;
  template: string;
  song: string;
  track: string;
  background: string;
  showPicture: boolean;
  startCash?: number;
  border?: string;
  description?: string;
  /** Stamped by the caller (the render layer owns the clock). */
  now?: number;
}

export function newProject(o: NewProjectOpts): CreatorProject {
  return {
    v: 1,
    id: o.id,
    name: o.name.trim(),
    template: o.template,
    song: o.song,
    track: o.track,
    background: o.background,
    showPicture: o.showPicture,
    pieces: templatePieces(findTemplate(o.template).levelId),
    plots: templatePlots(o.template, o.track),
    waves: [],
    peaceful: false,
    ...(o.startCash === undefined ? {} : { startCash: clampStartCash(o.startCash) }),
    ...(o.border === undefined ? {} : { border: findBorder(o.border).id }),
    ...(o.description ? { description: cleanDescription(o.description) } : {}),
    updatedAt: o.now ?? 0,
  };
}

/**
 * Change a project's template: its template pieces and plots are REPLACED,
 * the player's own pieces stay.
 */
export function applyTemplate(
  p: CreatorProject,
  templateId: string,
  trackId: string = p.track,
): CreatorProject {
  const own = p.pieces.filter((x) => !x.id.startsWith('tpl-'));
  return {
    ...p,
    template: templateId,
    track: trackId,
    pieces: [...templatePieces(findTemplate(templateId).levelId), ...own],
    // Blank's plots follow the track, so they must be laid along the NEW one.
    plots: templatePlots(templateId, trackId),
  };
}

/** The plots a TEST run can actually use: on the 1280×720 map. */
export function onMapPlots(p: Pick<CreatorProject, 'plots'>): Vec2[] {
  return p.plots.filter((q) => q.x >= 0 && q.x <= 1280 && q.y >= 0 && q.y <= 720);
}

/** The settings screen's name check; `selfId` is the project being renamed. */
export function nameError(
  name: string,
  projects: readonly CreatorProject[],
  selfId?: string,
): string | null {
  const n = name.trim();
  if (n.length === 0) return EMPTY_NAME;
  if (n.length > MAX_NAME) return LONG_NAME;
  const lower = n.toLowerCase();
  if (projects.some((p) => p.id !== selfId && p.name.trim().toLowerCase() === lower)) {
    return DUPLICATE_NAME;
  }
  return null;
}

/** The project as a playable level, under the reserved test id. */
/**
 * The project as a playable level, under the reserved TEST id — or, for an
 * Explore run, `EXPLORE_LEVEL_ID`. Both bank nothing (`creator: true`).
 */
export function projectToLevel(
  p: Omit<CreatorProject, 'id' | 'updatedAt' | 'v'>,
  id: number = CREATOR_LEVEL_ID,
): LevelDef {
  const track = findTrack(p.track);
  const bg = findBackground(p.background);
  return {
    id,
    name: p.name,
    creator: true,
    music: p.song === 'none' ? '' : p.song,
    background: bg.key,
    creatorScene: {
      bgColor: bg.color,
      bgKey: bg.key,
      pictureKey: projectPictureKey(p),
      pieces: p.pieces,
    },
    trackStyle: track.trackStyle,
    path: track.path,
    entrances: track.entrances,
    plots: p.plots.map((pos, id) => ({ id, pos: { x: pos.x, y: pos.y } })),
    startCash: startCashOf(p),
    startLives: CREATOR_START_LIVES,
    coinReward: 0,
    expReward: 0,
    towerHp: HUB_TOWER_HP,
    peaceful: p.peaceful || undefined,
    waves: p.waves.map((w) => ({
      groups: arenaWaveGroups(w.entries, w.interval),
      mods: w.mods,
    })),
  };
}

// ── Enemies met ──────────────────────────────────────────────────────────────

/** Every enemy a met enemy can bring onto the board: summons, splits, forms. */
function withChildren(types: EnemyType[]): EnemyType[] {
  const out = [...types];
  for (let i = 0; i < out.length; i++) {
    const spec = BALANCE.enemies[out[i]] as
      | {
          summon?: { enemyType: EnemyType; also?: EnemyType[] };
          hpSummon?: { enemyType: EnemyType };
          transformsInto?: { enemyType: EnemyType };
          splitsInto?: { enemyType: EnemyType };
        }
      | undefined;
    if (!spec) continue;
    const kids = [
      spec.summon?.enemyType,
      ...(spec.summon?.also ?? []),
      spec.hpSummon?.enemyType,
      spec.transformsInto?.enemyType,
      spec.splitsInto?.enemyType,
    ];
    for (const k of kids) if (k && !out.includes(k) && BALANCE.enemies[k]) out.push(k);
  }
  return out;
}

/** The wave creator's enemy list: everything in the levels the player has entered. */
export function metEnemyTypes(enteredIds: readonly number[]): EnemyType[] {
  const seen: EnemyType[] = [];
  for (const id of enteredIds) {
    const level = levelById(id);
    if (!level) continue;
    for (const w of level.waves) {
      for (const g of w.groups) if (!seen.includes(g.enemyType)) seen.push(g.enemyType);
    }
    if (level.roamer && !seen.includes(level.roamer.enemyType)) seen.push(level.roamer.enemyType);
  }
  return withChildren(seen);
}

// ── Editor operations (spec 2026-11-02 §2) ───────────────────────────────────

/** A selection: pieces by id, plots by index. */
export interface CreatorSelection {
  pieces: string[];
  plots: number[];
}

export const EMPTY_SELECTION: CreatorSelection = { pieces: [], plots: [] };

export function selectionSize(s: CreatorSelection): number {
  return s.pieces.length + s.plots.length;
}

/** FLIP: mirror every selected piece left-right (a second press undoes it). */
export function flipPieces(
  pieces: readonly CreatorPiece[],
  ids: readonly string[],
): CreatorPiece[] {
  const set = new Set(ids);
  return pieces.map((p) => {
    if (!set.has(p.id)) return p;
    const out: CreatorPiece = { ...p };
    if (p.flip) delete out.flip;
    else out.flip = true;
    return out;
  });
}

/**
 * ▲ / ▼: a piece's depth is its layer plus its place in the list, so the
 * front of its layer is the END of the list and the back is the START. The
 * moved pieces keep their order among themselves.
 */
export function reorderPieces(
  pieces: readonly CreatorPiece[],
  ids: readonly string[],
  to: 'front' | 'back',
): CreatorPiece[] {
  const set = new Set(ids);
  const moved = pieces.filter((p) => set.has(p.id));
  const rest = pieces.filter((p) => !set.has(p.id));
  return to === 'front' ? [...rest, ...moved] : [...moved, ...rest];
}

/**
 * ▲ / ▼ in the editor. First to the front / back of the piece's own layer;
 * pressed again when already there, a DECORATION climbs onto the TOP layer
 * (above the track, behind the top decorations — one more ▲ puts it in front
 * of them), and a TOP piece goes down under the track the same way. Textboxes
 * never change layer. Returns new piece objects only for those whose layer
 * changed.
 */
export function stepLayer(
  pieces: readonly CreatorPiece[],
  ids: readonly string[],
  to: 'front' | 'back',
): { pieces: CreatorPiece[]; crossed: PieceLayer | null } {
  const reordered = reorderPieces(pieces, ids, to);
  const order = (list: readonly CreatorPiece[], layer: PieceLayer) =>
    list
      .filter((p) => p.layer === layer)
      .map((p) => p.id)
      .join(',');
  const moved = (['decor', 'top', 'text'] as const).some(
    (l) => order(pieces, l) !== order(reordered, l),
  );
  if (moved) return { pieces: reordered, crossed: null };
  const from: PieceLayer = to === 'front' ? 'decor' : 'top';
  const dest: PieceLayer = to === 'front' ? 'top' : 'decor';
  const set = new Set(ids);
  const climbing = pieces.filter((p) => set.has(p.id) && p.layer === from);
  if (climbing.length === 0) return { pieces: [...pieces], crossed: null };
  const lifted = climbing.map((p) => ({ ...p, layer: dest }));
  const rest = pieces.filter((p) => !(set.has(p.id) && p.layer === from));
  // Up: the BACK of top (list start). Down: the FRONT of decor (list end).
  return {
    pieces: to === 'front' ? [...lifted, ...rest] : [...rest, ...lifted],
    crossed: dest,
  };
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A rectangle from two corners, whichever way the drag went. */
export function rectFrom(a: Vec2, b: Vec2): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

const inside = (r: Rect, q: Vec2) =>
  q.x >= r.x && q.x <= r.x + r.w && q.y >= r.y && q.y <= r.y + r.h;

/** Multi-select: every piece and plot whose CENTRE is inside the box. */
export function selectInRect(
  p: Pick<CreatorProject, 'pieces' | 'plots'>,
  r: Rect,
): CreatorSelection {
  return {
    pieces: p.pieces.filter((pc) => inside(r, pc)).map((pc) => pc.id),
    plots: p.plots.flatMap((q, i) => (inside(r, q) ? [i] : [])),
  };
}

/** What Ctrl+C holds: copies laid out around their centre. */
export interface CreatorClip {
  pieces: CreatorPiece[];
  plots: Vec2[];
}

export function copySelection(
  p: Pick<CreatorProject, 'pieces' | 'plots'>,
  sel: CreatorSelection,
): CreatorClip | null {
  const ids = new Set(sel.pieces);
  const pieces = p.pieces.filter((pc) => ids.has(pc.id));
  const plots = sel.plots.flatMap((i) => (p.plots[i] ? [p.plots[i]] : []));
  const all: Vec2[] = [...pieces, ...plots];
  if (all.length === 0) return null;
  const xs = all.map((q) => q.x);
  const ys = all.map((q) => q.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return {
    pieces: pieces.map((pc) => ({
      ...pc,
      colors: pc.colors && { ...pc.colors },
      x: pc.x - cx,
      y: pc.y - cy,
    })),
    plots: plots.map((q) => ({ x: q.x - cx, y: q.y - cy })),
  };
}

/**
 * Ctrl+V: the clip centred on `at`, every piece a fresh id. The caller adds
 * them (minding the caps) and selects them.
 */
export function pasteClip(
  clip: CreatorClip,
  at: Vec2,
  newId: () => string,
): { pieces: CreatorPiece[]; plots: Vec2[] } {
  return {
    pieces: clip.pieces.map((pc) => {
      const out: CreatorPiece = {
        ...pc,
        id: newId(),
        x: Math.round(at.x + pc.x),
        y: Math.round(at.y + pc.y),
      };
      if (pc.colors) out.colors = { ...pc.colors };
      else delete out.colors;
      return out;
    }),
    plots: clip.plots.map((q) => ({ x: Math.round(at.x + q.x), y: Math.round(at.y + q.y) })),
  };
}

/**
 * Water and river pieces: the grid they snap to, else null. HALF their size,
 * so a piece steps half a block at a time and can sit flush with the map's
 * edge (a whole-block grid put its centre on the edge, half of it off-map).
 */
export function snapGrid(key: string, scale: number): number | null {
  const it = findItem(key);
  if (!it?.snap) return null;
  return Math.max(2, Math.round((it.size * scale) / 2));
}

export function snapPoint(x: number, y: number, grid: number | null): Vec2 {
  if (!grid) return { x, y };
  return { x: Math.round(x / grid) * grid, y: Math.round(y / grid) * grid };
}

/** A textbox's words, as the server will keep them: one line, bounded. */
export function cleanText(raw: string): string {
  return raw
    .replace(/[\x00-\x1F\x7F]/g, ' ')
    .trim()
    .slice(0, TEXT_MAX);
}

// ── Explore (spec 2026-11-02 §4) ─────────────────────────────────────────────

/** Plays needed for the 1st…5th star. */
export const STAR_THRESHOLDS = [10, 25, 50, 100, 150] as const;
export const RED_STAR_PLAYS = 250;
export const BLUE_STAR_PLAYS = 500;

export type StarColor = 'gold' | 'red' | 'blue';
export const STAR_COLORS: Record<StarColor, string> = {
  gold: '#ffd23f',
  red: '#ff4a4a',
  blue: '#4da6ff',
};

export function starsForPlays(plays: number): number {
  return STAR_THRESHOLDS.filter((t) => plays >= t).length;
}

export function starColorFor(plays: number): StarColor {
  if (plays >= BLUE_STAR_PLAYS) return 'blue';
  if (plays >= RED_STAR_PLAYS) return 'red';
  return 'gold';
}

/** What a row draws: an admin's `/rate star` sticks; else the plays decide. */
export function displayStars(
  plays: number,
  override: number | null | undefined,
): { count: number; color: StarColor } {
  const count = override ?? starsForPlays(plays);
  return { count: Math.max(0, Math.min(5, count)), color: starColorFor(plays) };
}

export const HAMBI_LABELS = ['Easy', 'Medium', 'Mild', 'Hard', 'Extreme'] as const;

/** A difficulty's word, or N/A when an admin has not rated it. */
export function hambiLabel(hambis: number | null | undefined): string {
  if (hambis == null || hambis < 1 || hambis > 5) return 'N/A';
  return HAMBI_LABELS[hambis - 1];
}

/** A level's four-character code from what someone typed (`#k7q2` → `K7Q2`). */
export function normalizeCode(raw: string): string | null {
  const c = raw.trim().replace(/^#/, '').toUpperCase();
  return /^[BCDFGHJKLMNPQRSTVWXYZ2-9]{4}$/.test(c) ? c : null;
}

/** The creator's own view of one of their levels on the server. */
export interface PublishedRow {
  sourceId: string;
  code: string;
  live: boolean;
  banned: boolean;
  requestKind: 'publish' | 'unban' | null;
  plays: number;
}

export type PublishState = 'none' | 'pending' | 'live' | 'banned' | 'asked';

/** The control the creator menu shows under a level (spec §4.1). */
export function publishState(row: PublishedRow | undefined): PublishState {
  if (!row) return 'none';
  if (row.banned) return row.requestKind === 'unban' ? 'asked' : 'banned';
  if (row.requestKind === 'publish') return 'pending';
  return row.live ? 'live' : 'none';
}

/** Editing is frozen while an unban request waits (the server enforces it). */
export function isLocked(row: PublishedRow | undefined): boolean {
  return publishState(row) === 'asked';
}

/** A published level as Explore lists it. */
export interface ExploreRow {
  code: string;
  name: string;
  username: string;
  plays: number;
  starOverride: number | null;
  hambis: number | null;
  trending: boolean;
  /** For the row's map wash (the approved snapshot's). */
  background: string;
  template: string;
  showPicture: boolean;
  /** How many players have beaten it (Verified). */
  verified: number;
  /** Its border colour id. */
  border: string;
}

/** A level's saved `def` (what the server keeps) as a project-shaped value. */
export function defToProject(
  name: string,
  def: Record<string, unknown>,
): Omit<CreatorProject, 'id' | 'updatedAt'> {
  return {
    v: 1,
    name,
    template: typeof def.template === 'string' ? def.template : 'blank',
    song: typeof def.song === 'string' ? def.song : 'none',
    track: typeof def.track === 'string' ? def.track : CREATOR_TRACKS[0].id,
    background: typeof def.background === 'string' ? def.background : 'white',
    showPicture: def.showPicture !== false,
    pieces: Array.isArray(def.pieces) ? (def.pieces as CreatorPiece[]) : [],
    plots: Array.isArray(def.plots) ? (def.plots as Vec2[]) : [],
    waves: Array.isArray(def.waves) ? (def.waves as CreatorWave[]) : [],
    peaceful: def.peaceful === true,
    ...(typeof def.startCash === 'number' ? { startCash: clampStartCash(def.startCash) } : {}),
    ...(typeof def.border === 'string' ? { border: findBorder(def.border).id } : {}),
  };
}

// ── Round 4 (spec 2026-11-03-creator-chapters-design.md) ─────────────────────

/** Border colours — mirrored by SQL creator_border_ok (pinned by a test). */
export const CREATOR_BORDERS: readonly { id: string; label: string; color: number }[] = [
  { id: 'violet', label: 'Violet', color: 0x7b6cff },
  { id: 'red', label: 'Red', color: 0xe04848 },
  { id: 'orange', label: 'Orange', color: 0xff9a3c },
  { id: 'yellow', label: 'Yellow', color: 0xffd23f },
  { id: 'green', label: 'Green', color: 0x4caf50 },
  { id: 'teal', label: 'Teal', color: 0x2fb8a6 },
  { id: 'sky', label: 'Sky', color: 0x4da6ff },
  { id: 'blue', label: 'Blue', color: 0x3a5bd9 },
  { id: 'pink', label: 'Pink', color: 0xff6fb5 },
  { id: 'purple', label: 'Purple', color: 0xa855f7 },
  { id: 'white', label: 'White', color: 0xffffff },
  { id: 'black', label: 'Black', color: 0x222222 },
];
export const DEFAULT_BORDER = 'violet';

export function findBorder(id: string | undefined): (typeof CREATOR_BORDERS)[number] {
  return CREATOR_BORDERS.find((b) => b.id === id) ?? CREATOR_BORDERS[0];
}

/** A description's length — mirrored by creator_clean_description. */
export const MAX_DESCRIPTION = 150;

/** A description as the server keeps it: no control characters but line breaks. */
export function cleanDescription(raw: string): string {
  return raw
    .replace(/[\x00-\x09\x0B-\x1F\x7F]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, MAX_DESCRIPTION);
}

/**
 * What a rated Explore level pays on a player's first win, by its difficulty
 * (Hambis). Never shown while browsing — only on the win screen.
 */
export const EXPLORE_REWARDS: Record<number, { coins: number; exp: number }> = {
  1: { coins: 25, exp: 200 },
  2: { coins: 50, exp: 300 },
  3: { coins: 100, exp: 400 },
  4: { coins: 150, exp: 500 },
  5: { coins: 300, exp: 750 },
};

export function exploreRewardFor(
  hambis: number | null | undefined,
): { coins: number; exp: number } | null {
  return hambis == null ? null : (EXPLORE_REWARDS[hambis] ?? null);
}

/** "VERIFIED" once one player has beaten it, "VERIFIED - 4" for more, else ''. */
export function verifiedLabel(n: number): string {
  if (!Number.isFinite(n) || n < 1) return '';
  return n === 1 ? 'VERIFIED' : `VERIFIED - ${Math.floor(n)}`;
}

/** `/rate stars N` sets the plays to the Nth star's threshold (SQL mirrors it). */
export function playsForStars(n: number): number {
  return n <= 0 ? 0 : STAR_THRESHOLDS[Math.min(5, n) - 1];
}

// ── Chapters ──

export const MAX_CHAPTER_LEVELS = 30;
export const MAX_CHAPTERS = 10;

export const CHAPTER_FORMATS = [
  { id: 'episodes', label: 'Episodes', word: 'Episode' },
  { id: 'levels', label: 'Levels', word: 'Level' },
  { id: 'stages', label: 'Stages', word: 'Stage' },
] as const;
export type ChapterFormat = (typeof CHAPTER_FORMATS)[number]['id'];

export const CHAPTER_ORDERS = [
  { id: 'roman', label: 'Roman (I, II, III)' },
  { id: 'numbers', label: 'Numbers (1, 2, 3)' },
  { id: 'letters', label: 'Letters (A, B, C)' },
] as const;
export type ChapterOrder = (typeof CHAPTER_ORDERS)[number]['id'];

/** A chapter as its maker keeps it: an ordered list of published level codes. */
export interface ChapterDef {
  levels: string[];
  border: string;
  format: ChapterFormat;
  order: ChapterOrder;
}

export interface CreatorChapter extends ChapterDef {
  id: string;
  name: string;
  updatedAt: number;
}

export function emptyChapterDef(): ChapterDef {
  return { levels: [], border: DEFAULT_BORDER, format: 'episodes', order: 'roman' };
}

export function toRoman(n: number): string {
  const table: [number, string][] = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];
  let out = '';
  let rest = Math.max(1, Math.floor(n));
  for (const [v, s] of table) {
    while (rest >= v) {
      out += s;
      rest -= v;
    }
  }
  return out;
}

/** A, B … Z, AA, AB … */
export function toLetters(n: number): string {
  let rest = Math.max(1, Math.floor(n));
  let out = '';
  while (rest > 0) {
    const r = (rest - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    rest = Math.floor((rest - 1) / 26);
  }
  return out;
}

/** "Stage IX", "Level 9", "Episode I" — the `index`-th (0-based) level's label. */
export function chapterLevelLabel(format: string, order: string, index: number): string {
  const word = CHAPTER_FORMATS.find((f) => f.id === format)?.word ?? 'Episode';
  const n = index + 1;
  const mark = order === 'numbers' ? String(n) : order === 'letters' ? toLetters(n) : toRoman(n);
  return `${word} ${mark}`;
}

/** A chapter's def off the wire, re-validated (anything odd → the defaults). */
export function parseChapterDef(raw: unknown): ChapterDef {
  const d = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const levels = Array.isArray(d.levels)
    ? d.levels.filter((c): c is string => typeof c === 'string' && normalizeCode(c) === c)
    : [];
  return {
    levels: [...new Set(levels)].slice(0, MAX_CHAPTER_LEVELS),
    border: findBorder(typeof d.border === 'string' ? d.border : undefined).id,
    format: CHAPTER_FORMATS.some((f) => f.id === d.format)
      ? (d.format as ChapterFormat)
      : 'episodes',
    order: CHAPTER_ORDERS.some((o) => o.id === d.order) ? (d.order as ChapterOrder) : 'roman',
  };
}

/** Add a level (no duplicates, at most 30). Returns the def unchanged if refused. */
export function addChapterLevel(def: ChapterDef, code: string): ChapterDef {
  if (def.levels.includes(code) || def.levels.length >= MAX_CHAPTER_LEVELS) return def;
  return { ...def, levels: [...def.levels, code] };
}

/** One level of a published chapter as Explore serves it (`name` null = unavailable). */
export interface ChapterLevelRef {
  code: string;
  name: string | null;
}

/**
 * Which levels of a chapter are open: the first, and each one after a won
 * level. An unavailable level is skipped over (it neither blocks nor counts).
 */
export function chapterUnlocked(
  levels: readonly ChapterLevelRef[],
  won: readonly string[],
): boolean[] {
  const out: boolean[] = [];
  let open = true;
  for (const l of levels) {
    if (l.name === null) {
      out.push(false);
      continue;
    }
    out.push(open);
    open = open && won.includes(l.code);
  }
  return out;
}

/** A published chapter as Explore's CHAPTERS tab lists it. */
export interface ExploreChapterRow {
  code: string;
  name: string;
  username: string;
  plays: number;
  starOverride: number | null;
  hambis: number | null;
  trending: boolean;
  border: string;
  levelCount: number;
}
